import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deletePlayerCard, listPlayerCards, savePlayerCard, searchLeaguePlayers } from '../api/portal';
import { ApiError } from '../api/client';
import { copyText } from '../lib/clipboard';

export function PlayerCardsPage() {
  const qc = useQueryClient();
  const cards = useQuery({ queryKey: ['portal-player-cards'], queryFn: listPlayerCards });
  const [q, setQ] = useState('');
  const [personId, setPersonId] = useState('');
  const [label, setLabel] = useState('');
  const [logo, setLogo] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const found = useQuery({
    queryKey: ['portal-player-search', q],
    queryFn: () => searchLeaguePlayers(q),
    enabled: q.trim().length >= 2,
  });
  const save = useMutation({
    mutationFn: () => savePlayerCard({
      person_id: personId,
      sponsor_label: label.trim(),
      logo_url: logo.trim() || undefined,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['portal-player-cards'] });
      setErr(res.page_url ? `Kartica: ${res.page_url}` : 'Sačuvano. Liga mora imati javni slug da kartica dobije link.');
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });
  const remove = useMutation({
    mutationFn: deletePlayerCard,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-player-cards'] }),
  });

  return (
    <div>
      <div className="page-head">
        <div className="page-kicker">Liga</div>
        <h1 className="page-title">Kartice igrača</h1>
        <p className="page-sub">
          Jedna javna stranica po igraču: fotografija, golovi, sedmerci i do tri klipa.
          Liga proda slot sponzoru na toj kartici i pošalje link.
        </p>
      </div>
      <form
        className="card mb-4 grid gap-3 p-4"
        onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}
      >
        <div>
          <label className="label">Igrač</label>
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Prezime, bar 2 slova" />
          {(found.data?.players ?? []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {found.data!.players.map((p) => (
                <button
                  type="button"
                  key={p.person_id}
                  className={`btn-ghost text-xs ${personId === p.person_id ? 'text-white' : ''}`}
                  onClick={() => { setPersonId(p.person_id); setQ(p.full_name); }}
                >
                  {p.full_name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="label">Sponzor</label>
            <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Pekara kod Marka" required />
          </div>
          <div>
            <label className="label">Logo URL (opciono)</label>
            <input className="input" value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="https://…" />
          </div>
        </div>
        {err && <div className="text-sm text-slate-300">{err}</div>}
        <button className="btn-primary w-fit" disabled={save.isPending || !personId || !label.trim()}>Objavi karticu</button>
      </form>
      <div className="card p-4">
        {(cards.data ?? []).map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
            <div>
              <div className="text-white">{c.full_name}</div>
              <div className="text-xs text-slate-400">{c.sponsor_label}</div>
            </div>
            <div className="flex gap-2">
              {c.page_url && (
                <button type="button" className="btn-secondary text-xs" onClick={() => { void copyText(c.page_url); }}>
                  Kopiraj link
                </button>
              )}
              <button type="button" className="btn-ghost text-xs" onClick={() => remove.mutate(c.person_id)}>Ukloni</button>
            </div>
          </div>
        ))}
        {(cards.data ?? []).length === 0 && <div className="text-sm text-slate-500">Nema kartica.</div>}
      </div>
    </div>
  );
}
